import React, { useEffect, useState } from "react";
import {
  Link
} from "react-router-dom";
import {
  Plus,
  Pencil,
  Calendar
} from "lucide-react";
import * as ownerApi from "../../api/payroll/payPeriodApi";
import * as portalApi from "../../api/payroll/payPeriodPortalApi";
import {
  Button,
  IconButton,
  Card,
  CardContent,
  Table,
  Badge,
  EmptyState
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

export default function PayPeriodList({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const base = isPortal ? "/staff-panel/pay-periods" : "/pay-periods";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getAllPayPeriods();
      setList(Array.isArray(res) ? res : res?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortal]);

  // ---- Presentation-only derivations below (no data/logic changes) ----

  const tableRows = list.map((p) => ({ ...p, id: p._id }));

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (p) => <span className="font-semibold text-neutral-900 dark:text-neutral-100">{p.name}</span>,
    },
    {
      key: "type",
      header: "Type",
      render: (p) => (
        <Badge variant="default" className="font-mono">
          {p.type}
        </Badge>
      ),
    },
    {
      key: "isDefault",
      header: "Default",
      render: (p) => <Badge variant={p.isDefault ? "primary" : "default"}>{p.isDefault ? "Yes" : "No"}</Badge>,
    },
    {
      key: "active",
      header: "Active",
      render: (p) => <Badge variant={p.active ? "success" : "default"}>{p.active ? "Active" : "Inactive"}</Badge>,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (p) => (
        <Link to={`${base}/edit/${p._id}`}>
          <IconButton aria-label="Edit pay period" variant="ghost" size="sm">
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        </Link>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      {/* Top Header */}
      <div className="flex flex-col gap-4 border-b border-neutral-200 dark:border-neutral-800 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
            Pay Periods
          </h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Configure default salary payment frequencies and monthly cutoff schedules
          </p>
        </div>

        <Link to={`${base}/create`}>
          <Button type="button" variant="primary" leftIcon={<Plus className="h-4 w-4" />}>
            Add Pay Period
          </Button>
        </Link>
      </div>

      

      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            isLoading={loading}
            emptyState={
              <EmptyState
                icon={Calendar}
                title="No pay periods created yet"
                description="Add a pay period to define payment frequency and cutoff schedules."
                action={
                  <Link to={`${base}/create`}>
                    <Button variant="primary" size="sm" leftIcon={<Plus className="h-4 w-4" />}>
                      Add Pay Period
                    </Button>
                  </Link>
                }
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}